import { motion } from "framer-motion";
import { Instagram, Linkedin, Youtube, Link2, ArrowUpRight, Mail, Phone } from "lucide-react";
import {
  PROFILE,
  BRANDS,
  CASE_STUDIES,
  FEATURED_WORK,
  WHY_CHOOSE,
  SPECIAL_BUNDLE,
  ALT_PACKAGE,
  A_LA_CARTE,
  USAGE_RIGHTS,
} from "@/data";

const SHOTS = {
  followers:
    "https://customer-assets.emergentagent.com/job_creator-pricing-9/artifacts/v3dobm7f_IMG_7915%202.webp",
  country1:
    "https://customer-assets.emergentagent.com/job_creator-pricing-9/artifacts/nagilc16_IMG_7912%202.jpg",
  country2:
    "https://customer-assets.emergentagent.com/job_creator-pricing-9/artifacts/ll5jz2s5_IMG_7914%202.jpg",
};

const ACCOMPLISHMENTS = [
  "250k+ followers across socials",
  "100M+ views through my content",
  "10M+ views for sponsored content",
  "30M+ views on pre-made content used from brands on paid ads",
  "10,000+ conversions in sign-ups",
];

const fade = {
  hidden: { opacity: 0, y: 14 },
  show: { opacity: 1, y: 0, transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] } },
};

function Block({ children, className = "" }) {
  return (
    <motion.div
      variants={fade}
      initial="hidden"
      whileInView="show"
      viewport={{ once: true, margin: "-60px" }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

function Heading({ children, testid }) {
  return (
    <Block className="mt-12 mb-3">
      <h2 className="nt-h2" data-testid={testid}>
        <span className="nt-highlight">{children}</span>
      </h2>
    </Block>
  );
}

function Bullet({ children }) {
  return (
    <li className="flex gap-3 leading-relaxed">
      <span className="mt-[9px] w-[5px] h-[5px] rounded-full bg-[#37352f] shrink-0" />
      <span className="nt-p">{children}</span>
    </li>
  );
}

export default function MediaKit() {
  return (
    <div data-testid="media-kit-page" className="relative pb-32">
      {/* Cover */}
      <div
        data-testid="cover-banner"
        className="h-[22vh] min-h-[150px] w-full"
        style={{
          background:
            "linear-gradient(135deg, #f5e2c6 0%, #eec38b 55%, #e4a866 100%)",
        }}
      />

      <div className="max-w-[760px] mx-auto px-5 sm:px-10">
        {/* Profile photo overlapping cover */}
        <div className="-mt-[54px] mb-3" data-testid="page-icon">
          <img
            src={PROFILE.image}
            alt={PROFILE.name}
            className="w-24 h-24 sm:w-28 sm:h-28 rounded-2xl object-cover ring-4 ring-white shadow-[0_10px_34px_rgba(0,0,0,0.22)]"
          />
        </div>

        {/* Title */}
        <Block>
          <h1
            data-testid="page-title"
            className="font-extrabold tracking-tight text-4xl sm:text-5xl text-[#37352f]"
          >
            Fabion&apos;s Media Kit
          </h1>
        </Block>

        {/* Quick stats */}
        <Block className="mt-4">
          <div className="flex flex-wrap gap-2" data-testid="quick-stats">
            {[
              "250k+ followers",
              "100M+ views",
              "100+ AI brands",
              "10,000+ conversions",
            ].map((s) => (
              <span
                key={s}
                className="inline-flex items-center rounded-full border border-[rgba(55,53,47,0.16)] bg-[#f7f6f3] px-3 py-1 text-[13px] font-medium text-[#37352f]"
              >
                {s}
              </span>
            ))}
          </div>
        </Block>

        {/* Bio */}
        <Block className="mt-6">
          <p className="nt-p text-[16px]">{PROFILE.bio} Beyond broad consumer reach, my content
          consistently attracts decision-makers and creative professionals who actively adopt new AI
          tools — making it an ideal environment for brands seeking both mass awareness and high-intent
          users.</p>
        </Block>

        {/* Social links */}
        <Heading testid="heading-social">Social Media Links:</Heading>
        <Block>
          <ul className="space-y-2.5">
            <li className="flex items-center gap-2.5">
              <Instagram size={18} className="text-[#E1306C] shrink-0" />
              <span className="text-[#787774]">Instagram</span>
              <a href={PROFILE.socials.instagram} target="_blank" rel="noreferrer" className="nt-link" data-testid="social-instagram">
                Fabion Kallaku (@fabionkallaku) • Instagram profile
              </a>
            </li>
            <li className="flex items-center gap-2.5">
              <Link2 size={18} className="text-[#787774] shrink-0" />
              <a href={PROFILE.socials.linkedin} target="_blank" rel="noreferrer" className="nt-link" data-testid="social-linkedin">
                www.linkedin.com
              </a>
            </li>
            <li className="flex items-center gap-2.5">
              <Youtube size={18} className="text-[#FF0000] shrink-0" />
              <span className="text-[#787774]">YouTube</span>
              <a href={PROFILE.socials.youtube} target="_blank" rel="noreferrer" className="nt-link" data-testid="social-youtube">
                Fabion Kallaku
              </a>
            </li>
          </ul>
        </Block>

        {/* Accomplishments */}
        <Heading testid="heading-accomplishments">Accomplishments and results:</Heading>
        <Block>
          <p className="nt-p mb-4">
            Worked with 100+ AI companies such as: {BRANDS.slice(0, 10).join(", ")} and way more…
          </p>
          <ul className="space-y-2">
            {ACCOMPLISHMENTS.map((a) => (
              <Bullet key={a}>{a}</Bullet>
            ))}
          </ul>
        </Block>

        {/* Case studies */}
        <Heading testid="heading-casestudies">Case studies:</Heading>
        <Block>
          <p className="nt-p mb-4">Featured brand collaborations and content:</p>
          <div className="grid sm:grid-cols-2 gap-2">
            {FEATURED_WORK.map((w) => (
              <a
                key={w.title}
                href={w.link}
                target="_blank"
                rel="noreferrer"
                data-testid={`featured-${w.title.toLowerCase().replace(/\s+/g, "-")}`}
                className="nt-callout group flex items-center justify-between gap-2 px-4 py-3 hover:bg-[#efeeea] transition-colors"
              >
                <span className="flex items-center gap-2.5">
                  <Instagram size={16} className="text-[#E1306C]" />
                  <span className="text-[15px] font-medium text-[#37352f]">{w.title}</span>
                </span>
                <ArrowUpRight size={15} className="text-[#9b9a97] group-hover:text-[#37352f] transition-colors" />
              </a>
            ))}
          </div>

          <div className="grid sm:grid-cols-2 gap-3 mt-4">
            {CASE_STUDIES.map((cs) => (
              <div key={cs.brand} className="nt-callout p-4 flex flex-col" data-testid={`case-${cs.brand.toLowerCase()}`}>
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-[17px]">🎯</span>
                  <span className="font-semibold text-[#37352f]">{cs.brand}</span>
                </div>
                <p className="text-[14px] text-[#787774] leading-relaxed mb-3">{cs.description}</p>
                {cs.metrics && (
                  <div className="flex flex-wrap gap-x-6 gap-y-1">
                    {cs.metrics.map((m) => (
                      <span key={m.label} className="text-[14px]">
                        <span className="font-bold text-[#37352f]">{m.value}</span>{" "}
                        <span className="text-[#787774]">{m.label.toLowerCase()}</span>
                      </span>
                    ))}
                  </div>
                )}
                {cs.link && (
                  <a
                    href={cs.link}
                    target="_blank"
                    rel="noreferrer"
                    data-testid={`case-link-${cs.brand.toLowerCase()}`}
                    className="mt-auto inline-flex items-center gap-1.5 text-[13px] font-medium text-[#37352f] nt-link w-fit pt-1"
                  >
                    View Campaign
                    <ArrowUpRight size={13} />
                  </a>
                )}
              </div>
            ))}
          </div>
        </Block>

        {/* Audience insights */}
        <Heading testid="heading-audience">Audience insights:</Heading>
        <Block>
          <p className="nt-p mb-6">
            Real platform analytics — an audience that buys, not just scrolls. High-intent adopters
            skewing toward founders, builders, and decision-makers, concentrated in top buying markets.
          </p>

          <div className="grid sm:grid-cols-2 gap-5 items-start">
            {/* Followers: gender + active times */}
            <div data-testid="audience-shot-followers">
              <p className="text-[13px] uppercase tracking-wide font-semibold text-[#9b9a97] mb-2">
                Gender &amp; active times
              </p>
              <div className="nt-callout overflow-hidden p-0 max-w-[240px]">
                <img
                  src={SHOTS.followers}
                  alt="Follower demographics — gender split (Men 73.3%, Women 26.7%) and active times"
                  loading="lazy"
                  className="w-full block"
                />
              </div>
            </div>

            {/* Top countries */}
            <div className="space-y-4">
              <div data-testid="audience-shot-country-1">
                <p className="text-[13px] uppercase tracking-wide font-semibold text-[#9b9a97] mb-2">
                  Top countries
                </p>
                <div className="nt-callout overflow-hidden p-2.5 max-w-[320px]">
                  <img
                    src={SHOTS.country1}
                    alt="Audience by country — US 41.4%, India 10.7%, Albania 8.6%, Mexico 6.5%, Belarus 4.6%"
                    loading="lazy"
                    className="w-full block rounded"
                  />
                </div>
              </div>
              <div data-testid="audience-shot-country-2">
                <div className="nt-callout overflow-hidden p-2.5 max-w-[320px]">
                  <img
                    src={SHOTS.country2}
                    alt="Audience by country — US 46.5%, UK 16.5%, Canada 14.0%, Australia 8.9%, India 3.3%"
                    loading="lazy"
                    className="w-full block rounded"
                  />
                </div>
              </div>
            </div>
          </div>
        </Block>

        {/* Why work with me */}
        <Heading testid="heading-why">Why work with me:</Heading>
        <Block>
          <ul className="space-y-2">
            {WHY_CHOOSE.map((w) => (
              <Bullet key={w}>{w}</Bullet>
            ))}
          </ul>
        </Block>

        {/* Investment / Rate card */}
        <Heading testid="heading-pricing">Investment &amp; rate card:</Heading>

        {/* Special bundle */}
        <Block>
          <div
            data-testid="special-bundle"
            className="rounded-lg border-2 border-[#e4923a] bg-[#fdf3e6] p-5 sm:p-6"
          >
            <div className="flex items-center gap-2 mb-1">
              <span className="text-[18px]">⭐</span>
              <span className="text-[12px] uppercase tracking-wider font-bold text-[#c2721f]">Best value — one-time offer</span>
            </div>
            <h3 className="font-bold text-xl text-[#37352f]">{SPECIAL_BUNDLE.title}</h3>
            <p className="text-[14px] text-[#787774] mt-1 mb-4">{SPECIAL_BUNDLE.subtitle}</p>
            <div className="flex items-end gap-3 mb-1">
              <span className="font-extrabold text-4xl text-[#37352f]">{SPECIAL_BUNDLE.priceDiscounted}</span>
              <span className="text-xl text-[#9b9a97] line-through mb-1">{SPECIAL_BUNDLE.priceRegular}</span>
            </div>
            <p className="text-[13px] font-semibold text-[#c2721f] mb-4">{SPECIAL_BUNDLE.savings}</p>
            <ul className="space-y-2">
              {SPECIAL_BUNDLE.includes.map((inc) => (
                <li key={inc} className="flex gap-2.5 text-[15px] text-[#37352f]">
                  <span className="text-[#e4923a]">✓</span>
                  {inc}
                </li>
              ))}
            </ul>
          </div>
        </Block>

        {/* Alt package */}
        <Block className="mt-4">
          <div
            data-testid="alt-package"
            className="rounded-lg border border-[rgba(55,53,47,0.16)] bg-white p-5 sm:p-6"
          >
            <span className="text-[12px] uppercase tracking-wider font-semibold text-[#9b9a97]">Package</span>
            <h3 className="font-bold text-xl text-[#37352f] mt-1">{ALT_PACKAGE.title}</h3>
            <p className="text-[14px] text-[#787774] mt-1 mb-4">{ALT_PACKAGE.subtitle}</p>
            <div className="flex items-end gap-3 mb-1">
              <span className="font-extrabold text-4xl text-[#37352f]">{ALT_PACKAGE.priceDiscounted}</span>
              <span className="text-xl text-[#9b9a97] line-through mb-1">{ALT_PACKAGE.priceRegular}</span>
            </div>
            <p className="text-[13px] font-semibold text-[#37352f] mb-4">{ALT_PACKAGE.savings}</p>
            <ul className="space-y-2">
              {ALT_PACKAGE.includes.map((inc) => (
                <li key={inc} className="flex gap-2.5 text-[15px] text-[#37352f]">
                  <span className="text-[#9b9a97]">✓</span>
                  {inc}
                </li>
              ))}
            </ul>
          </div>
        </Block>

        {/* A la carte table */}
        <Block className="mt-6">
          <p className="text-[13px] uppercase tracking-wide font-semibold text-[#9b9a97] mb-2">Rate card</p>
          <div className="rounded-md border border-[rgba(55,53,47,0.16)] overflow-hidden" data-testid="table-alacarte">
            {A_LA_CARTE.map((row, i) => (
              <div
                key={row.service}
                className={`flex items-center justify-between px-4 py-3 ${i !== 0 ? "border-t border-[rgba(55,53,47,0.09)]" : ""}`}
              >
                <span className="text-[15px] text-[#37352f]">{row.service}</span>
                <span className="font-semibold text-[15px] text-[#37352f]">{row.price}</span>
              </div>
            ))}
          </div>
        </Block>

        {/* Usage rights table */}
        <Block className="mt-6">
          <p className="text-[13px] uppercase tracking-wide font-semibold text-[#9b9a97] mb-2">Usage rights</p>
          <div className="rounded-md border border-[rgba(55,53,47,0.16)] overflow-hidden" data-testid="table-usage">
            {USAGE_RIGHTS.map((row, i) => (
              <div
                key={row.service}
                className={`flex items-start justify-between gap-4 px-4 py-3 ${i !== 0 ? "border-t border-[rgba(55,53,47,0.09)]" : ""}`}
              >
                <div className="min-w-0">
                  <div className="text-[15px] text-[#37352f]">{row.service}</div>
                  {row.subtitle && (
                    <div className="text-[13px] text-[#9b9a97] mt-0.5">{row.subtitle}</div>
                  )}
                </div>
                <span className="font-semibold text-[15px] text-[#37352f] whitespace-nowrap">{row.price}</span>
              </div>
            ))}
          </div>
        </Block>

        {/* Contact */}
        <Heading testid="heading-contact">Let&apos;s collaborate:</Heading>
        <Block>
          <div className="nt-callout p-5">
            <p className="nt-p mb-4">Ready to build something that converts? Reach out directly:</p>
            <div className="flex flex-col gap-3">
              <a href={`mailto:${PROFILE.email}`} className="flex items-center gap-2.5 nt-link w-fit" data-testid="contact-email">
                <Mail size={17} className="text-[#787774]" />
                {PROFILE.email}
              </a>
              <a href={`tel:${PROFILE.phone.replace(/[^+\d]/g, "")}`} className="flex items-center gap-2.5 nt-link w-fit" data-testid="contact-phone">
                <Phone size={17} className="text-[#787774]" />
                {PROFILE.phone}
              </a>
            </div>
            <div className="flex flex-wrap gap-3 mt-5">
              {[
                { icon: Instagram, href: PROFILE.socials.instagram, label: "Instagram", color: "#E1306C" },
                { icon: Linkedin, href: PROFILE.socials.linkedin, label: "LinkedIn", color: "#0A66C2" },
                { icon: Youtube, href: PROFILE.socials.youtube, label: "YouTube", color: "#FF0000" },
              ].map(({ icon: Icon, href, label, color }) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noreferrer"
                  data-testid={`contact-social-${label.toLowerCase()}`}
                  className="inline-flex items-center gap-2 rounded-md border border-[rgba(55,53,47,0.16)] bg-white px-4 py-2 text-[14px] font-medium text-[#37352f] hover:bg-[#f7f6f3] transition-colors"
                >
                  <Icon size={16} style={{ color }} />
                  {label}
                </a>
              ))}
            </div>
          </div>
        </Block>

        <div className="mt-16 pt-6 border-t border-[rgba(55,53,47,0.09)] text-[13px] text-[#9b9a97]">
          © {new Date().getFullYear()} {PROFILE.name} — Media Kit
        </div>
      </div>
    </div>
  );
}
